import {FormGroup, FormLabel, InputLabel, MenuItem, Alert} from "@mui/material";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import { useTranslation } from "react-i18next";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import {useEffect, useState} from "react";
import SelectionData from "../../interfaces/Graphs/SelectionData";

enum SelectType {
    AllSelect, MultiSelect, IntervalSelect
}

interface GraphControlProps {
    name: string,
    label: string,
    useInterval : boolean,
    selectionData: SelectionData[],
    emitChange: Function,
    isAnimals?: boolean
}

export default function GraphControl(props : GraphControlProps) {

    const {t} = useTranslation("traffic");
    const [controlType, SetControlType] = useState<SelectType>(SelectType.AllSelect);
    const [dataSelect, SetDataSelect] = useState<string[]>([]);
    const [dataIntervalFrom, SetDataIntervalFrom] = useState<string[]>(["2021"]);
    const [dataIntervalTo, SetDataIntervalTo] = useState<string[]>(["2022"]);
    const [intervalError, SetIntervalError] = useState<string>("");

    const OnChangeHandler = (event : SelectChangeEvent<typeof dataSelect>) => {
        const {target: {value}} = event;
        if (props.isAnimals && (props.name === "Region" || props.name === "Rok" || props.name === "Year")) {
            // For animal regions and years, use single select 
            SetDataSelect([value as string]);
        } else {
            // For normal multi-select behavior
            SetDataSelect(typeof value === "string" ? value.split(",") : value);
        }
    }

    const OnChangeIntervalFromHandler = (event : SelectChangeEvent<typeof dataIntervalFrom>) => {
        const {target: {value}} = event;
        const newFromValue = typeof value === "string" ? value.split(",") : value;
        const fromYear = parseInt(newFromValue[0]);
        const toYear = parseInt(dataIntervalTo[0]);
        
        SetIntervalError("");
        
        // Validate: "from" year cannot be greater than "to" year
        if (fromYear <= toYear) {
            SetDataIntervalFrom(newFromValue);
        } else {
            // If invalid, adjust "to" year to match "from" year and show warning
            SetDataIntervalFrom(newFromValue);
            SetDataIntervalTo([newFromValue[0]]);
            SetIntervalError(`Rok "od" (${fromYear}) bol upravený na rok "do" (${toYear}).`);
            
            // Clear error after 3 seconds
            setTimeout(() => SetIntervalError(""), 3000);
        }
    }

    const OnChangeIntervalToHandler = (event : SelectChangeEvent<typeof dataIntervalTo>) => {
        const {target: {value}} = event;
        const newToValue = typeof value === "string" ? value.split(",") : value;
        const toYear = parseInt(newToValue[0]);
        const fromYear = parseInt(dataIntervalFrom[0]);
        
        SetIntervalError("");
        
        // Validate: "to" year cannot be less than "from" year
        if (toYear >= fromYear) {
            SetDataIntervalTo(newToValue);
        } else {
            // If invalid, adjust "from" year to match "to" year and show warning
            SetDataIntervalTo(newToValue);
            SetDataIntervalFrom([newToValue[0]]);
            SetIntervalError(`Rok "do" (${toYear}) bol upravený na rok "od" (${fromYear}).`);
            
            // Clear error after 3 seconds
            setTimeout(() => SetIntervalError(""), 3000);
        }
    }

    const EmitChanges = (data : Object) => {
        props.emitChange({Name: props.name, Option: controlType, Data: data});
    }
    useEffect(() => {
        switch (controlType) {
            case SelectType.AllSelect:
                EmitChanges({});
                break;
            case SelectType.MultiSelect:
                EmitChanges(dataSelect);
                break;
            case SelectType.IntervalSelect:
                EmitChanges([dataIntervalFrom[0], dataIntervalTo[0]]);
                break;
            default:
                return;
        }
    }, [controlType])
    useEffect(() => {
        EmitChanges(dataSelect);
    }, [dataSelect])
    useEffect(() => {
        // Generate array of all years in interval for IntervalSelect
        const generateYearRange = (from: string, to: string): string[] => {
            const fromYear = parseInt(from);
            const toYear = parseInt(to);
            const years: string[] = [];
            
            for (let year = fromYear; year <= toYear; year++) {
                years.push(year.toString());
            }
            return years;
        };
        
        const yearRange = generateYearRange(dataIntervalFrom[0], dataIntervalTo[0]);
        EmitChanges(yearRange);
    }, [dataIntervalFrom, dataIntervalTo])


    const onControlChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        let type;
        switch (event.target.value) {
            case "0":
                type = SelectType.AllSelect;
                break;
            case "1":
                type = SelectType.MultiSelect;
                break;
            case "2":
                type = SelectType.IntervalSelect;
                break;
            default:
                type = SelectType.AllSelect;
                break;
        }
        SetControlType(type);
    };

    return (
        <div className="graph_control">
            <FormControl sx={{m:1, width: 'auto'}}>
                <FormLabel>{props.label}</FormLabel>
                <RadioGroup row>
                    <FormControlLabel value={SelectType.AllSelect} control={<Radio onChange={onControlChangeHandler} checked={controlType === SelectType.AllSelect}/>} label={t("Graph.vsetko")} />
                    <FormControlLabel value={SelectType.MultiSelect} control={<Radio onChange={onControlChangeHandler} checked={controlType === SelectType.MultiSelect}/>} label={t("Graph.vyber")} />
                    {
                        props.useInterval &&
                        (
                            <FormControlLabel value={SelectType.IntervalSelect} control={<Radio onChange={onControlChangeHandler} checked={controlType === SelectType.IntervalSelect}/>} label={t("Graph.interval")} />
                        )
                    }
                </RadioGroup>

                {
                    controlType === SelectType.MultiSelect &&
                    (
                        <FormControl sx={{ m: 1, width: 'auto' }}>
                            <InputLabel id={`${props.label}SelectLabel`}>{props.label}</InputLabel>
                            <Select
                                multiple={!(props.isAnimals && (props.name === "Region" || props.name === "Rok" || props.name === "Year"))}
                                value={props.isAnimals && (props.name === "Region" || props.name === "Rok" || props.name === "Year") ? (dataSelect[0] || "") as any : dataSelect}
                                onChange={OnChangeHandler}
                                labelId={`${props.label}SelectLabel`}
                                label={props.label}
                                autoWidth
                            >
                                {
                                    props.selectionData.map((item) => (
                                        <MenuItem
                                            key={item.Type}
                                            value={item.Code}
                                        >
                                            {item.Type}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )
                }
                {
                    controlType === SelectType.IntervalSelect &&
                    (
                        <FormGroup>
                            <FormControl sx={{ m: 1, width: 'auto' }}>
                                <InputLabel id={`${props.label}Interval1`}>{`${props.label +" " + t("Graph.od")} `}</InputLabel>
                                <Select
                                    value={dataIntervalFrom}
                                    onChange={OnChangeIntervalFromHandler}
                                    labelId={`${props.label}Interval1`}
                                    label={`${props.label + " " +t("Graph.do")}`}
                                    autoWidth
                                >
                                    {
                                        props.selectionData.map((item) => (
                                            <MenuItem
                                                key={item.Type}
                                                value={item.Code}
                                            >
                                                {item.Type}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ m: 1, width: 'auto' }}>
                                <InputLabel id={`${props.label}Interval2`}>{`${props.label+" " + t("Graph.do")}`}</InputLabel>
                                <Select
                                    value={dataIntervalTo}
                                    onChange={OnChangeIntervalToHandler}
                                    labelId={`${props.label}Interval2`}
                                    label={`${props.label + " " + t("Graph.od")}`}
                                    autoWidth
                                >
                                    {
                                        props.selectionData.map((item) => (
                                            <MenuItem
                                                key={item.Type}
                                                value={item.Code}
                                            >
                                                {item.Type}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                            {intervalError && (
                                <Alert severity="warning" sx={{ m: 1, width: 'auto' }}>
                                    {intervalError}
                                </Alert>
                            )}
                        </FormGroup>
                    )
                }
            </FormControl>
        </div>
    );
}